/**
 * Unread notification count for the navigation badge.
 *
 * `Notification/Count` answers with two numbers rather than a page of rows, so
 * the badge can be refreshed on navigation without the cost of GetNotifications.
 * There is no interval: a member who is not moving through the app is not
 * receiving anything they can act on either, and a returning tab refreshes on
 * focus.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router-dom';
import notificationApiService from '@wlb/shared/api/services/notificationApiService';

interface NotificationBadgeValue {
  unreadCount: number;
  /** Re-reads the count — call after marking notifications read. */
  refresh: () => Promise<void>;
}

const notificationBadgeContext = createContext<NotificationBadgeValue | null>(null);

const isDocumentVisible = (): boolean => document.visibilityState === 'visible';

/** Reads `unreadCount` off either envelope shape the API is known to answer with. */
const readUnreadCount = (payload: unknown): number | null => {
  if (payload == null || typeof payload !== 'object') return null;
  const direct = (payload as { unreadCount?: unknown }).unreadCount;
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return Math.max(0, direct);
  }
  const nested = (payload as { data?: unknown }).data;
  return nested != null && nested !== payload ? readUnreadCount(nested) : null;
};

export function NotificationBadgeProvider({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const requestRef = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    const pending = requestRef.current;
    if (pending) return pending;

    const request = (async () => {
      try {
        const response = await notificationApiService.getNotificationCount();
        const count = readUnreadCount(response?.data);
        if (count != null) setUnreadCount(count);
      } catch {
        // A badge is not worth an error state: keep the last known count.
      }
    })().finally(() => {
      requestRef.current = null;
    });

    requestRef.current = request;
    return request;
  }, []);

  // Every navigation is a cheap moment to be current, and the first run covers
  // app start / sign-in.
  useEffect(() => {
    if (!isDocumentVisible()) return;
    refresh().catch(() => undefined);
  }, [pathname, refresh]);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (isDocumentVisible()) refresh().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refresh]);

  const value = useMemo<NotificationBadgeValue>(
    () => ({ unreadCount, refresh }),
    [unreadCount, refresh],
  );

  return (
    <notificationBadgeContext.Provider value={value}>
      {children}
    </notificationBadgeContext.Provider>
  );
}

/**
 * Null outside the provider — the navigation renders for anonymous visitors
 * too, and they have no notifications to count.
 */
export function useNotificationBadge(): NotificationBadgeValue | null {
  return useContext(notificationBadgeContext);
}
